import { useLayoutEffect, useState, type ChangeEvent } from "react";
import { Card, CardProps } from "../Card";
import { Radio, RadioProps } from "../Radio";
import cx from "classnames";
import styles from "./SelectCard.module.scss";
import { Checkbox, CheckboxProps } from "../Checkbox";
import { forwardRef } from "react";
import { SelectCardGroup } from "./SelectCardGroup"; // Assuming SelectCardGroup is in a separate file

import { useSelectCardContext } from "./SelectCardProvider";
import { useOptionallyControlledState } from "../../hooks/useOptionallyControlledState";

/**
 * Props for the SelectCard component
 * @extends Omit<CardProps, "onChange" | "id">
 */
export interface SelectCardProps extends Omit<CardProps, "onChange" | "id"> {
  /**
   * Passes props to the underlying `<Checkbox />` component.
   */
  checkboxProps?: Omit<CheckboxProps, "label">;

  /**
   * Controlled state. When provided, the component becomes controlled.
   */
  checked?: boolean;

  /**
   * Uncontrolled state.
   */
  defaultChecked?: boolean;

  /**
   * When `true`, disables the card.
   */
  disabled?: boolean;

  /**
   * When `true`, sets error styling on the card.
   */
  errored?: boolean;

  /**
   * A unique identifier for the card, used to track the selection state.
   */
  id: string | number;

  /**
   * Function called on selection state change.
   */
  onChange: (value: boolean) => void;

  /**
   * Passes props to the underlying `<Radio />` component.
   */
  radioProps?: Omit<RadioProps, "label">;

  /**
   * Removes the drop shadow effect on the card.
   */
  removeDropShadow?: boolean;
}

const SelectCardElement = forwardRef<HTMLInputElement, SelectCardProps>(
  (props, ref) => {
    const {
      checkboxProps,
      children,
      checked: checkedProp,
      className,
      defaultChecked: defaultCheckedProp,
      disabled,
      errored,
      radioProps,
      onChange,
      removeDropShadow,
      id,
      ...rest
    } = props;

    const [checked, setChecked] = useOptionallyControlledState<boolean>({
      controlledValue: checkedProp,
      defaultValue: defaultCheckedProp || false,
      onChange,
    });

    const context = useSelectCardContext();

    // we need to re-render after setting the initial selection state on context
    const [_, setForceRenderCount] = useState(0);
    useLayoutEffect(() => {
      if (defaultCheckedProp) {
        context?.onChange(
          undefined,
          {
            id,
            checked: defaultCheckedProp,
          },
          { internal: true },
        );

        setForceRenderCount((prev) => prev + 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event?: ChangeEvent<HTMLInputElement>) => {
      const newChecked = event ? event.target.checked : false;
      setChecked(newChecked);
      context?.onChange(event, {
        id,
        checked: newChecked,
      });
    };

    // A selection in the group context supersedes the normal checked state of the card
    const isChecked = context ? context.selection.has(id) : checked;

    return (
      <>
        {(!context || context?.selectionMode === "multiple") && (
          <Checkbox
            {...checkboxProps}
            defaultChecked={defaultCheckedProp}
            onChange={handleChange}
            disabled={disabled}
            ref={ref}
            label={
              <Card
                {...rest}
                className={cx(styles["select-card"], className, {
                  [styles["select-card--remove-drop-shadow"]]: removeDropShadow,
                  [styles["select-card--selected"]]: isChecked && !errored,
                  [styles["select-card--errored"]]: errored,
                  [styles["select-card--disabled"]]: disabled,
                })}
              >
                {children}
              </Card>
            }
            hideCheckbox
          />
        )}
        {context?.selectionMode === "single" && (
          <Radio
            {...radioProps}
            defaultChecked={defaultCheckedProp}
            onChange={handleChange}
            disabled={disabled}
            ref={ref}
            name={context.radioName}
            label={
              <Card
                {...rest}
                className={cx(styles["select-card"], className, {
                  [styles["select-card--remove-drop-shadow"]]: removeDropShadow,
                  [styles["select-card--selected"]]: isChecked && !errored,
                  [styles["select-card--errored"]]: errored,
                  [styles["select-card--disabled"]]: disabled,
                })}
              >
                {children}
              </Card>
            }
            hideRadio
          />
        )}
      </>
    );
  },
);

SelectCardElement.displayName = "SelectCard";

/**
 * SelectCard component for creating interactive card-based selection interfaces.
 *
 * Features:
 * - Supports both single selection (radio) and multiple selection (checkbox) modes
 * - Automatic context detection for selection behavior
 * - Visual feedback for selected, disabled, and error states
 * - Accessible with proper ARIA roles and keyboard navigation
 * - Customizable styling through Card component props
 * - Hover and focus states with smooth transitions
 * - Integration with SelectCardGroup for grouped selection
 *
 * @example
 * <SelectCard
 *   id="option-1"
 *   onChange={(checked) => console.log('Selected:', checked)}
 * >
 *   <Text variant="headline" size="small">Option 1</Text>
 *   <Text>Description of option 1</Text>
 * </SelectCard>
 */
export const SelectCard = Object.assign(SelectCardElement, {
  /**
   * SelectCardGroup component for managing groups of selectable cards.
   *
   * Features:
   * - Manages selection state for multiple SelectCard components
   * - Supports both single and multiple selection modes
   * - Provides proper accessibility with legends and ARIA labels
   * - Handles required field validation
   * - Automatic radio button grouping for single selection
   * - Context provider for child SelectCard components
   *
   * @example
   * <SelectCard.Group
   *   legend="Choose your plan"
   *   selectionMode="single"
   *   onChange={(e, state) => console.log('Selection changed:', state)}
   * >
   *   <SelectCard id="basic" onChange={() => {}}>
   *     <Text variant="headline" size="small">Basic Plan</Text>
   *   </SelectCard>
   *   <SelectCard id="premium" onChange={() => {}}>
   *     <Text variant="headline" size="small">Premium Plan</Text>
   *   </SelectCard>
   * </SelectCard.Group>
   */
  Group: SelectCardGroup,
});
